import {
  type QueryKey,
  keepPreviousData,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNotificacionStore } from '@rfacil/shared/hooks';
import {
  CambioPreferenciaCorreo,
  ContadorBuzonDTO,
  EventoHistorialBuzonDTO,
  FiltroNotificacionesBuzon,
  NotificacionBuzonDTO,
  PaginaNotificacionesBuzonDTO,
  PreferenciaCorreoBuzonDTO,
  ResumenBuzonDTO,
  descartarNotificacion,
  getContadorBuzon,
  getHistorialBuzon,
  getNotificacionesBuzon,
  getPreferenciasCorreo,
  getResumenBuzon,
  guardarPreferenciasCorreo,
  marcarNotificacionesLeidas,
  marcarTodasLeidas,
} from '../services/buzonService';

/** Cada cuánto se refresca el contador de la campanita mientras la pestaña está a la vista. */
export const INTERVALO_CONTADOR_BUZON_MS = 60_000;

/**
 * Query keys del buzón.
 *
 * El buzón es del usuario del token, así que el usuario forma parte de la llave: sin él, al cambiar
 * de sesión en la misma pestaña se mostraría por un instante el contador del usuario anterior.
 * Todas cuelgan de `all`, de modo que una acción (leer, descartar) refresca contador, resumen y
 * lista con una sola invalidación.
 */
export const buzonKeys = {
  all: ['buzon'] as const,
  usuario: (idUsuario: number) => [...buzonKeys.all, idUsuario] as const,
  contador: (idUsuario: number) => [...buzonKeys.usuario(idUsuario), 'contador'] as const,
  resumen: (idUsuario: number) => [...buzonKeys.usuario(idUsuario), 'resumen'] as const,
  notificaciones: (idUsuario: number, filtro: FiltroNotificacionesBuzon) =>
    [
      ...buzonKeys.usuario(idUsuario),
      'notificaciones',
      filtro.categoria ?? null,
      filtro.estado ?? 'Pendiente',
      filtro.pagina ?? 1,
      filtro.tamano ?? null,
    ] as const,
  historial: (idUsuario: number, idEmpresa: number, tipoEntidad: string, idEntidad: string) =>
    [...buzonKeys.usuario(idUsuario), 'historial', idEmpresa, tipoEntidad, idEntidad] as const,
  preferencias: (idUsuario: number) => [...buzonKeys.usuario(idUsuario), 'preferencias'] as const,
};

/**
 * Llave de la mutación "marcar como leída". `useMarcandoLeidas` la busca para saber si hay un
 * marcado en curso en cualquier pantalla (el panel o `/buzon`).
 */
export const MUTACION_MARCAR_LEIDAS = [...buzonKeys.all, 'marcarLeidas'] as const;

/** Opciones de `useContadorBuzon`. */
export interface OpcionesContadorBuzon {
  /**
   * Refrescar cada minuto mientras la pestaña está a la vista. Solo la campanita lo pide: el aviso al
   * iniciar sesión, el panel y `/buzon` leen la misma caché.
   */
  sondeo?: boolean;
}

/**
 * Contador de la campanita, del aviso al iniciar sesión, del panel y de `/buzon` (una sola llave en
 * caché). Con `sondeo` se refresca cada minuto mientras la pestaña está a la vista; todos lo piden al
 * montar (con `staleTime: 0`) y al volver a la pestaña (`refetchOnWindowFocus` global). `habilitado`
 * evita la petición para roles sin buzón.
 */
export function useContadorBuzon(idUsuario: number, habilitado: boolean, { sondeo = false }: OpcionesContadorBuzon = {}) {
  return useQuery<ContadorBuzonDTO>({
    queryKey: buzonKeys.contador(idUsuario),
    queryFn: getContadorBuzon,
    enabled: habilitado && idUsuario > 0,
    staleTime: 0,
    refetchInterval: sondeo ? INTERVALO_CONTADOR_BUZON_MS : false,
    refetchIntervalInBackground: false,
  });
}

/** Categorías con sus contadores para la pantalla del buzón y el panel de la campanita. */
export function useResumenBuzon(idUsuario: number, habilitado: boolean) {
  return useQuery<ResumenBuzonDTO>({
    queryKey: buzonKeys.resumen(idUsuario),
    queryFn: getResumenBuzon,
    enabled: habilitado && idUsuario > 0,
    staleTime: 0,
  });
}

/**
 * Una página de notificaciones. Mientras llega la página siguiente se sigue mostrando la anterior
 * (`keepPreviousData`), para que la lista no parpadee al paginar o cambiar de filtro.
 */
export function useNotificacionesBuzon(
  idUsuario: number,
  filtro: FiltroNotificacionesBuzon,
  habilitado: boolean
) {
  return useQuery<PaginaNotificacionesBuzonDTO>({
    queryKey: buzonKeys.notificaciones(idUsuario, filtro),
    queryFn: () => getNotificacionesBuzon(filtro),
    enabled: habilitado && idUsuario > 0,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

/** Lo notificado sobre un registro, para el historial dentro del propio registro. */
export function useHistorialBuzon(
  idUsuario: number,
  idEmpresa: number | null | undefined,
  tipoEntidad: string,
  idEntidad: string | null | undefined,
  habilitado: boolean
) {
  return useQuery<EventoHistorialBuzonDTO[]>({
    queryKey: buzonKeys.historial(idUsuario, idEmpresa ?? 0, tipoEntidad, idEntidad ?? ''),
    queryFn: () => getHistorialBuzon(idEmpresa as number, tipoEntidad, idEntidad as string),
    enabled: habilitado && idUsuario > 0 && !!idEmpresa && idEmpresa > 0 && !!idEntidad,
    staleTime: 0,
  });
}

/** Preferencias de correo del usuario. */
export function usePreferenciasCorreo(idUsuario: number, habilitado: boolean) {
  return useQuery<PreferenciaCorreoBuzonDTO[]>({
    queryKey: buzonKeys.preferencias(idUsuario),
    queryFn: getPreferenciasCorreo,
    enabled: habilitado && idUsuario > 0,
    staleTime: 0,
  });
}

/** Una consulta del buzón en caché, tal como la regresa `getQueriesData`. */
type ConsultaEnCache = [queryKey: QueryKey, data: unknown];

/**
 * Un cambio del marcado inmediato en una consulta: su llave, qué consulta es, los datos de antes y los
 * nuevos. En una página, `nuevo.Elementos` es `previo.Elementos.map(...)`: mismo orden y, en las que no
 * cambian, el mismo objeto. `calcularReversion` se apoya en eso para saber cuáles cambió.
 */
type CambioEnCache =
  | { queryKey: QueryKey; tipo: 'notificaciones'; previo: PaginaNotificacionesBuzonDTO; nuevo: PaginaNotificacionesBuzonDTO }
  | { queryKey: QueryKey; tipo: 'contador'; previo: ContadorBuzonDTO; nuevo: ContadorBuzonDTO }
  | { queryKey: QueryKey; tipo: 'resumen'; previo: ResumenBuzonDTO; nuevo: ResumenBuzonDTO };

const esObjeto = (data: unknown): data is Record<string, unknown> => typeof data === 'object' && data !== null;

const esPaginaNotificaciones = (data: unknown): data is PaginaNotificacionesBuzonDTO =>
  esObjeto(data) && Array.isArray(data.Elementos);

const esContador = (data: unknown): data is ContadorBuzonDTO => esObjeto(data) && typeof data.NoLeidas === 'number';

const esResumen = (data: unknown): data is ResumenBuzonDTO =>
  esObjeto(data) && typeof data.TotalNoLeidas === 'number' && Array.isArray(data.Categorias);

/** Resta sin bajar de cero: el caché puede ir un poco atrás del SSO. */
const restar = (actual: number, cuanto: number): number => Math.max(0, actual - cuanto);

const sumar = (porCategoria: Map<string, number>): number =>
  Array.from(porCategoria.values()).reduce((total, n) => total + n, 0);

/**
 * De `ids`, cuántas siguen pendientes y sin leer en las páginas en caché, por usuario (segundo
 * elemento de la llave) y por categoría. Una notificación puede estar en varias páginas (el panel y
 * `/buzon`, "Todas" y su categoría): se cuenta una vez. Las que no están en caché, las ya leídas y
 * las que no son `Pendiente` no se cuentan (`NoLeidas` solo cuenta lo que sigue en el buzón); el
 * refresco de `onSettled` corrige cualquier diferencia.
 */
function contarSinLeerEnCache(consultas: ConsultaEnCache[], ids: ReadonlySet<number>): Map<unknown, Map<string, number>> {
  const contadas = new Set<number>();
  const porUsuario = new Map<unknown, Map<string, number>>();
  for (const [queryKey, data] of consultas) {
    if (queryKey[2] !== 'notificaciones' || !esPaginaNotificaciones(data)) {
      continue;
    }
    for (const n of data.Elementos) {
      if (!ids.has(n.Id) || n.Leida || n.Estado !== 'Pendiente' || contadas.has(n.Id)) {
        continue;
      }
      contadas.add(n.Id);
      const porCategoria = porUsuario.get(queryKey[1]) ?? new Map<string, number>();
      porCategoria.set(n.ClaveCategoria, (porCategoria.get(n.ClaveCategoria) ?? 0) + 1);
      porUsuario.set(queryKey[1], porCategoria);
    }
  }
  return porUsuario;
}

/**
 * Lo que cambia en el caché al marcar `ids` como leídas: en cada página en caché pasan a
 * `Leida: true` con `FechaLectura`, y el contador y el resumen del mismo usuario restan de
 * `NoLeidas` las que estaban sin leer, también por categoría. Las páginas se arman con
 * `Elementos.map` y dejan igual (el mismo objeto) lo que no cambia: ver `CambioEnCache`.
 */
function calcularLecturaInmediata(consultas: ConsultaEnCache[], ids: number[], fechaLectura: string): CambioEnCache[] {
  const porMarcar = new Set(ids);
  const sinLeer = contarSinLeerEnCache(consultas, porMarcar);
  const cambios: CambioEnCache[] = [];

  for (const [queryKey, data] of consultas) {
    const tipo = queryKey[2];
    const porCategoria = sinLeer.get(queryKey[1]);

    if (tipo === 'notificaciones' && esPaginaNotificaciones(data)) {
      if (data.Elementos.some((n) => porMarcar.has(n.Id) && !n.Leida)) {
        cambios.push({
          queryKey,
          tipo,
          previo: data,
          nuevo: {
            ...data,
            Elementos: data.Elementos.map((n) =>
              porMarcar.has(n.Id) && !n.Leida ? { ...n, Leida: true, FechaLectura: fechaLectura } : n
            ),
          },
        });
      }
    } else if (tipo === 'contador' && porCategoria && esContador(data)) {
      cambios.push({ queryKey, tipo, previo: data, nuevo: { ...data, NoLeidas: restar(data.NoLeidas, sumar(porCategoria)) } });
    } else if (tipo === 'resumen' && porCategoria && esResumen(data)) {
      cambios.push({
        queryKey,
        tipo,
        previo: data,
        nuevo: {
          ...data,
          TotalNoLeidas: restar(data.TotalNoLeidas, sumar(porCategoria)),
          Categorias: data.Categorias.map((c) => ({ ...c, NoLeidas: restar(c.NoLeidas, porCategoria.get(c.Clave) ?? 0) })),
        },
      });
    }
  }
  return cambios;
}

/**
 * Cómo deshacer el marcado inmediato de UNA mutación sobre lo que haya en caché cuando falla: en cada
 * página, solo sus notificaciones vuelven a su `Leida`/`FechaLectura` de antes; en el contador y el
 * resumen se suma de vuelta solo lo que ella restó. Así no se pisa otro marcado que siga en curso.
 * `dataUpdatedAt` es la hora de la respuesta del SSO sobre la que se marcó: si al fallar es otra, llegó
 * un refresco que no tiene este marcado y no hay nada que deshacer.
 */
type ReversionEnCache = { queryKey: QueryKey; dataUpdatedAt: number } & (
  | { tipo: 'notificaciones'; previas: ReadonlyMap<number, Pick<NotificacionBuzonDTO, 'Leida' | 'FechaLectura'>> }
  | { tipo: 'contador'; noLeidas: number }
  | { tipo: 'resumen'; totalNoLeidas: number; porCategoria: ReadonlyMap<string, number> }
);

/** Lo que una mutación cambió en una consulta, para deshacer solo eso (ver `ReversionEnCache`). */
function calcularReversion(cambio: CambioEnCache, dataUpdatedAt: number): ReversionEnCache {
  const { queryKey } = cambio;
  switch (cambio.tipo) {
    case 'notificaciones': {
      const previas = new Map<number, Pick<NotificacionBuzonDTO, 'Leida' | 'FechaLectura'>>();
      // Mismo orden y mismos objetos en lo que no cambió (ver `CambioEnCache`): distinto = lo marcó esta mutación.
      cambio.previo.Elementos.forEach((n, indice) => {
        if (cambio.nuevo.Elementos[indice] !== n) {
          previas.set(n.Id, { Leida: n.Leida, FechaLectura: n.FechaLectura });
        }
      });
      return { queryKey, dataUpdatedAt, tipo: cambio.tipo, previas };
    }
    case 'contador':
      return { queryKey, dataUpdatedAt, tipo: cambio.tipo, noLeidas: cambio.previo.NoLeidas - cambio.nuevo.NoLeidas };
    case 'resumen': {
      const porCategoria = new Map<string, number>();
      cambio.previo.Categorias.forEach((c, indice) => {
        porCategoria.set(c.Clave, c.NoLeidas - cambio.nuevo.Categorias[indice].NoLeidas);
      });
      const totalNoLeidas = cambio.previo.TotalNoLeidas - cambio.nuevo.TotalNoLeidas;
      return { queryKey, dataUpdatedAt, tipo: cambio.tipo, totalNoLeidas, porCategoria };
    }
  }
}

/** Aplica una reversión sobre los datos que hay ahora en caché (sin datos, no hay qué revertir). */
function revertirEnCache(actual: unknown, reversion: ReversionEnCache): unknown {
  if (reversion.tipo === 'notificaciones' && esPaginaNotificaciones(actual)) {
    return {
      ...actual,
      Elementos: actual.Elementos.map((n) => {
        const previa = reversion.previas.get(n.Id);
        return previa ? { ...n, ...previa } : n;
      }),
    };
  }
  if (reversion.tipo === 'contador' && esContador(actual)) {
    return { ...actual, NoLeidas: actual.NoLeidas + reversion.noLeidas };
  }
  if (reversion.tipo === 'resumen' && esResumen(actual)) {
    return {
      ...actual,
      TotalNoLeidas: actual.TotalNoLeidas + reversion.totalNoLeidas,
      Categorias: actual.Categorias.map((c) => ({ ...c, NoLeidas: c.NoLeidas + (reversion.porCategoria.get(c.Clave) ?? 0) })),
    };
  }
  return actual;
}

/**
 * Marca notificaciones como leídas. Sin toast: pasa al abrir una notificación y no debe interrumpir.
 * El caché se actualiza al instante (lista, contador y resumen) y, si el SSO falla, se deshace solo lo
 * que cambió esta mutación (otro marcado en curso conserva el suyo). Al terminar (también si falla)
 * se refrescan contador, resumen y lista.
 */
export function useMarcarLeidas() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MUTACION_MARCAR_LEIDAS,
    mutationFn: (ids: number[]) => marcarNotificacionesLeidas(ids),
    retry: false,
    onMutate: async (ids: number[]): Promise<ReversionEnCache[]> => {
      const fechaLectura = new Date().toISOString();
      const calcular = () =>
        calcularLecturaInmediata(queryClient.getQueriesData({ queryKey: buzonKeys.all }), ids, fechaLectura);
      // Que un refresco en vuelo no pise la actualización inmediata. Solo se cancela lo que esa
      // actualización cambia (páginas, contador y resumen del usuario dueño de esas notificaciones):
      // cancelar una consulta que aún no trae datos (el historial de una factura, las preferencias) la
      // regresa a su estado inicial, sin datos y sin cargar, y la pantalla la daría por vacía.
      await Promise.all(calcular().map(({ queryKey }) => queryClient.cancelQueries({ queryKey, exact: true })));
      // Se calcula de nuevo con lo que quedó tras cancelar: de ahí sale lo que se deshace si el SSO falla.
      return calcular().map((cambio) => {
        // El marcado inmediato conserva la hora de la respuesta del SSO: así, al fallar, un refresco
        // (otra hora) se distingue de este marcado o de otro que siga en curso (la misma).
        const dataUpdatedAt = queryClient.getQueryState(cambio.queryKey)?.dataUpdatedAt ?? 0;
        queryClient.setQueryData(cambio.queryKey, cambio.nuevo, { updatedAt: dataUpdatedAt });
        return calcularReversion(cambio, dataUpdatedAt);
      });
    },
    onError: (error: Error, _ids, reversiones) => {
      // Sobre el caché de ahora, no sobre la foto de `onMutate`: restaurar la foto completa borraría el
      // marcado inmediato de otra mutación que empezó después. Lo que se refrescó después del marcado
      // trae lo del SSO, sin la resta: se deja como vino. El refresco de `onSettled` corrige el resto.
      reversiones?.forEach((reversion) => {
        if (queryClient.getQueryState(reversion.queryKey)?.dataUpdatedAt !== reversion.dataUpdatedAt) {
          return;
        }
        queryClient.setQueryData(reversion.queryKey, (actual: unknown) => revertirEnCache(actual, reversion), {
          updatedAt: reversion.dataUpdatedAt,
        });
      });
      console.error('Error al marcar notificaciones como leídas:', error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: buzonKeys.all });
    },
  });
}

/**
 * Si hay un "marcar como leída" en curso en cualquier pantalla. Mientras dura, el contador en caché
 * trae la resta inmediata o, si el SSO falló, su reversión: la campanita no debe leerlos como
 * notificaciones nuevas.
 */
export function useMarcandoLeidas(): boolean {
  return useIsMutating({ mutationKey: MUTACION_MARCAR_LEIDAS }) > 0;
}

/** "Marcar todas como leídas", opcionalmente de una categoría. */
export function useMarcarTodasLeidas() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificacionStore();

  return useMutation({
    mutationFn: (categoria?: string | null) => marcarTodasLeidas(categoria),
    retry: false,
    onError: (error: Error) => {
      console.error('Error al marcar todas como leídas:', error);
      showNotification(error.message || 'No se pudieron marcar como leídas. Intenta nuevamente.', 'error');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: buzonKeys.all });
    },
  });
}

/** Descarta un aviso. Muestra el mensaje del SSO si es un pendiente accionable (400) o no es suyo (404). */
export function useDescartarNotificacion() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificacionStore();

  return useMutation({
    mutationFn: (idNotificacion: number) => descartarNotificacion(idNotificacion),
    retry: false,
    onError: (error: Error) => {
      console.error('Error al descartar la notificación:', error);
      showNotification(error.message || 'No se pudo descartar el aviso. Intenta nuevamente.', 'error');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: buzonKeys.all });
    },
  });
}

/** Guarda las preferencias de correo que cambiaron. */
export function useGuardarPreferenciasCorreo() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificacionStore();

  return useMutation({
    mutationFn: (cambios: CambioPreferenciaCorreo[]) => guardarPreferenciasCorreo(cambios),
    // Un 409 (dos guardados al mismo tiempo) se resuelve volviendo a cargar, no reenviando.
    retry: false,
    onSuccess: () => {
      showNotification('Preferencias de correo guardadas', 'success');
    },
    onError: (error: Error) => {
      console.error('Error al guardar las preferencias de correo:', error);
      showNotification(error.message || 'No se pudieron guardar las preferencias. Intenta nuevamente.', 'error');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: buzonKeys.all });
    },
  });
}
